/**
 * Mở URL ở TAB MỚI, kể cả khi URL chỉ có sau một bước bất đồng bộ (ký signed URL,
 * đọc blob offline...).
 *
 * Bẫy đã gặp: `window.open('', '_blank', 'noopener,noreferrer')` theo spec LUÔN
 * trả về `null` (noopener cắt liên kết với tab cha). Code cũ thấy null nên rơi
 * vào fallback `window.location.href = url` → file mở đè lên tab ứng dụng, BOD
 * phải bấm Back mới quay lại được.
 *
 * Cách đúng: mở tab trống KHÔNG kèm noopener (để giữ handle), rồi tự cắt
 * `opener` bằng tay. Nếu trình duyệt chặn popup thì fallback bằng thẻ <a
 * target="_blank"> — không bao giờ điều hướng tab hiện tại.
 */

/** Gọi ĐỒNG BỘ trong sự kiện click (trước mọi `await`) để iOS Safari không chặn popup. */
export function prepareNewTab(): Window | null {
  const win = window.open('', '_blank');
  if (win) {
    try {
      win.opener = null;
    } catch {
      /* một số WebView không cho ghi opener — bỏ qua */
    }
    paintLoading(win);
  }
  return win;
}

/**
 * Vẽ trang chờ vào tab trống. Từ khi bỏ signed URL, file phải tải xong mới mở
 * được (Drive cũ qua edge function có thể mất vài giây); tab trắng trong lúc đó
 * khiến người dùng tưởng app không phản ứng.
 */
function paintLoading(win: Window): void {
  try {
    const doc = win.document;
    doc.open();
    doc.write(LOADING_HTML);
    doc.close();
  } catch {
    /* WebView không cho ghi — bỏ qua, chỉ mất trang chờ */
  }
}

const LOADING_HTML = `<!doctype html><html lang="vi"><head><meta charset="utf-8">
<title>Đang tải báo cáo…</title>
<style>
  html,body{height:100%;margin:0;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;background:#f8fafc;color:#334155}
  .wrap{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px}
  .spin{width:40px;height:40px;border:4px solid #cbd5e1;border-top-color:#05985C;border-radius:50%;animation:r .8s linear infinite}
  @keyframes r{to{transform:rotate(360deg)}}
  p{margin:0;font-size:15px} small{color:#64748b}
</style></head><body><div class="wrap"><div class="spin"></div>
<p>Đang tải báo cáo…</p><small>Vui lòng đợi trong giây lát, không cần bấm lại.</small></div></body></html>`;

/** Đưa URL vào tab đã mở sẵn; không có tab thì mở tab mới bằng thẻ <a>. */
export function navigateNewTab(win: Window | null, url: string): void {
  if (win && !win.closed) {
    win.location.href = url;
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Mở URL đã có sẵn ở tab mới (trường hợp không cần await). */
export function openInNewTab(url: string): void {
  navigateNewTab(prepareNewTab(), url);
}
